// Vue
import { defineStore } from 'pinia'
import * as Sentry from '@sentry/browser'

//Services
import FirebaseUtils from '@/utils/FirebaseUtils'
import AxiosInstance from '@/services/AxiosInstance.js'
import FullScreen from '@/services/FullScreen.js'
import Logger, { logServerEvent } from '@/services/Logger.js'

//types
import type { ApiPupilScreener, progressSateError, DeviceInfo } from '@/customTypes'

// stores
import { useDataStore } from '@/stores/DataStore'
import { useDevStore } from '@/stores/DevStore'
import { useMediaDevicesStore } from './MediaDevicesStore'

const logger = new Logger('MableScreener.vue', true)
const url = `${_API_BASE_URL_}/screening/room` as string
const screenerRoomsPath = 'screenerRooms'

interface RedirectLocation {
  name?: string
  params: Record<string, any>
  query: Record<string, any>
}

interface screenerState {
  activeScreener: ScreenerData | null
  isConnected: boolean
  authFailed: boolean
  roomId: number | null
  error: string | null
  screenerWatcher: any | null
  demoWatcher: any | null
  screenerId: number | null
  pupilScreenerId: number | null
  activeCode: string | null
  dataStore: ReturnType<typeof useDataStore>
  devStore: ReturnType<typeof useDevStore>
  mediaStore: ReturnType<typeof useMediaDevicesStore>
  visibilityState: boolean
  focusState: boolean
  connectionId: string
  takeOverRequestId: string | null
  redirectFrom: RedirectLocation | null
  sentryUserData: any
  lastTeacherCheck: number
}

interface ScreenerData {
  state: 'Completed' | 'Paused' | 'Finishing' | 'Active' | 'Refresh'
  error?: ScreenerError
  warning?: ScreenerError
  status: 'Disconnected' | 'Active' | 'Completed' | 'Error' | 'Warning'
  action?: 'Refresh' | 'Play' | 'Pause'
  id: string | number
  code: number
  section: string
  progress: number
  name: string
  classId: number
  questionCount: number
  lastUpdated?: Date | string
  lastDisconnected?: Date | string
  connectionId: string
  teacherSynced?: boolean
}

interface ScreenerError {
  type: string
  message: string
  priority?: number
  metadata?: {
    serviceUrl?: string
    [key: string]: any
  }
}

interface TeacherData {
  connectedAt?: number
  lastSeen?: number
  lastDisconnected?: number
  status: 'Active' | 'Disconnected'
}

const getDeviceInformation = () => {
  const { userAgent, platform } = navigator
  const userAgentData = navigator.userAgentData

  const getOS = () => {
    if (userAgentData) {
      return userAgentData.platform
    }
    if (userAgent.includes('Win')) return 'Windows'
    if (userAgent.includes('Mac')) return 'macOS'
    if (userAgent.includes('Linux')) return 'Linux'
    if (userAgent.includes('Android')) return 'Android'
    if (userAgent.includes('like Mac')) return 'iOS'
    return 'Unknown'
  }

  const getDeviceType = () => {
    if (userAgentData) {
      return userAgentData.mobile ? 'Mobile' : 'Desktop'
    }
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent)) {
      return 'Tablet'
    }
    if (
      /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
        userAgent
      )
    ) {
      return 'Mobile'
    }
    return 'Desktop'
  }

  const deviceUUID = localStorage.getItem('mable_device_uuid') || 'unknown'

  const info: {
    os: string
    deviceType: string
    platform: string
    userAgent: string
    deviceUUID: string
    userAgentData?: {
      brands: string
      mobile: boolean
      platform: string
    }
  } = {
    os: getOS(),
    deviceType: getDeviceType(),
    platform,
    userAgent,
    deviceUUID
  }

  if (userAgentData) {
    info.userAgentData = {
      brands: userAgentData.brands.map((b) => `${b.brand} ${b.version}`).join(', '),
      mobile: userAgentData.mobile,
      platform: userAgentData.platform
    }
  }

  return info
}

export const useActiveScreenerStore = defineStore('screener', {
  state: (): screenerState => ({
    activeScreener: null,
    isConnected: false,
    authFailed: false,
    roomId: null,
    error: null,
    screenerWatcher: null,
    demoWatcher: null,
    screenerId: null,
    pupilScreenerId: null,
    activeCode: null,
    visibilityState: false,
    focusState: false,
    dataStore: useDataStore(),
    devStore: useDevStore(),
    mediaStore: useMediaDevicesStore(),
    connectionId: '',
    takeOverRequestId: null,
    redirectFrom: null,
    sentryUserData: {},
    lastTeacherCheck: 0
  }),

  actions: {
    async logToDatabase(event: string, data: any) {
      await logServerEvent(this.pupilScreenerId, event, data)
    },

    async initScreener(authData: {
      code: string
      method: string
      demo: Boolean
    }): Promise<ApiPupilScreener> {
      logger.info('Initializing connection with auth:', authData)

      try {
        const auth = {
          ...authData,
          currentCode: this.activeCode,
          role: 'screener',
          takeOverId: this.takeOverRequestId
        }

        const response = await AxiosInstance.post(`${url}/join`, auth)

        if (this.activeCode && authData.code !== this.activeCode) {
          await this.disconnectWatcher()
        }

        if (response.success) {
          return await this.setupScreenerConnection(response, authData)
        } else {
          throw new Error('Failed to connect')
        }
      } catch (error: any) {
        const { status, message } = error
        logger.info('error', message)

        if ([401, 409, 500].includes(status)) {
          logger.info('error has error code', error)
          this.authFailed = true
          throw error
        } else {
          throw new Error(
            'Oops, something went wrong while connecting to the screener.\n Please refresh the page and try again. If that doesn’t work, you can regenerate a new code.'
          )
        }
      }
    },

    async setupScreenerConnection(
      response: { success: boolean; screenerData: ApiPupilScreener },
      authData: { code: string; method: string }
    ) {
      this.isConnected = true
      this.authFailed = false
      this.roomId = response.screenerData.class_id
      this.screenerId = response.screenerData.pupil_id
      this.pupilScreenerId = response.screenerData.id
      this.activeCode = authData.code
      this.takeOverRequestId = null

      this.screenerWatcher = FirebaseUtils.createSyncedObject(
        null,
        `${screenerRoomsPath}/${this.roomId}/screeners/${this.screenerId}`
      )

      this.screenerWatcher.on((data: any) => {
        if (!data) return this.handleScreenerRemoved()

        this.handleIncomingData(data)
        this.activeScreener = data
      })

      const updates = {
        status: 'Disconnected',
        warning: null,
        lastDisconnected: FirebaseUtils.serverTimestamp()
      }

      this.screenerWatcher.setOnDisconnectHandler('update', updates, (error: any) => {
        if (error) {
          logger.error('Failed to set onDisconnect handler for teacher:', error)
        } else {
          logger.info('onDisconnect handler set successfully for teacher')
        }
      })

      await this.checkAndUpdateScreenerState()

      if (this.devStore.isDemoSession) {
        this.demoWatcher = FirebaseUtils.createSyncedObject(
          null,
          `screenerDemoCodes/${authData.code}`
        )

        this.demoWatcher.setOnDisconnectHandler('remove', null, (error: any) => {
          if (error) {
            logger.error('Failed to set onDisconnect handler for teacher:', error)
          } else {
            logger.info('onDisconnect handler set successfully for teacher')
          }
        })
      }

      this.setSentryUserData(response.screenerData)
      await this.logToDatabase('device_information', getDeviceInformation())
      return response.screenerData
    },

    async logTeacherDisconnection(data: Record<string, any>) {
      await this.logToDatabase('teacher_disconnected', data)
      await this.updateFirebase({ teacherSynced: false })
    },

    async checkTeacherActivity() {
      try {
        const now = Date.now()
        const CHECK_INTERVAL = 30000 // Check every 30 seconds

        // Only check if it's been at least 30 seconds since last check
        if (now - this.lastTeacherCheck < CHECK_INTERVAL) {
          return
        }

        this.lastTeacherCheck = now

        if (!this.roomId) {
          return
        }

        const teacherPath = `${screenerRoomsPath}/${this.roomId}/teachers`

        let teachers: Record<string, TeacherData> | null = null

        try {
          const teachersRef = FirebaseUtils.createSyncedObject(null, teacherPath)
          teachers = await new Promise<Record<string, TeacherData> | null>((resolve) => {
            teachersRef.once((val: Record<string, TeacherData> | null) => {
              resolve(val)
            }, false)
          })
        } catch (fetchError: any) {
          logger.error('Failed to fetch teacher data:', {
            error: fetchError?.message,
            path: teacherPath
          })
          return
        }

        if (!teachers || Object.keys(teachers).length === 0) {
          await this.logTeacherDisconnection({ reason: 'no_teacher_exists' })
          return
        }

        const TWO_MINUTES = 2 * 60 * 1000

        for (const [teacherId, teacherData] of Object.entries(teachers)) {
          if (!teacherData.lastSeen) {
            await this.logTeacherDisconnection({
              teacherId,
              reason: 'no_last_seen'
            })
            continue
          }

          const timeSinceLastSeen = now - teacherData.lastSeen

          if (timeSinceLastSeen > TWO_MINUTES && teacherData.status === 'Active') {
            logger.info(
              `Teacher ${teacherId} inactive for ${Math.round(timeSinceLastSeen / 1000)}s, logging disconnection`
            )
            await this.logTeacherDisconnection({
              teacherId,
              lastSeen: teacherData.lastSeen,
              timeSinceLastSeen,
              reason: 'timeout'
            })
          }
        }
      } catch (error: any) {
        logger.error('Error checking teacher activity:', error?.message)
      }
    },

    async handleIncomingData(data: any) {
      if (this.connectionId && data.connectionId !== this.connectionId)
        return this.handleScreenerRemoved()
      if (data.status === 'Disconnected') {
        this.reconnectScreener()
      }

      // Check teacher activity when we receive updates from Firebase
      this.checkTeacherActivity()

      if (data.ping) {
        logger.info('Received ping', data.ping)
        await this.updateFirebase({
          pingResponse: data.ping,
          ping: null
        })
      }

      if (data.action) {
        switch (data.action) {
          case 'Paused':
            logger.info('Pausing screener')
            this.dataStore.setPausedState(true)
            await this.confirmAction('Paused')
            break
          case 'Active':
            logger.info('Resuming screener')
            this.dataStore.setPausedState(false)
            await this.confirmAction('Active')
            if (data.error?.type) {
              await this.sendScreenerResolvedError({
                type: data.error.type
              })
            }
            if (data.warning?.type) {
              await this.sendScreenerResolvedWarning({
                type: data.warning.type
              })
            }
            break
          case 'Refresh':
            logger.info('Refreshing screener')
            await this.confirmAction('Refresh')

            FullScreen.cancel()
            window.location.reload()
            break
          default:
            logger.info('Unknown action:', data.action)
        }
      }
    },

    async confirmAction(action: string) {
      try {
        logger.info('confirming action', action)
        await this.updateFirebase({
          state: action,
          action: null
        })
        logger.info(`Action ${action} confirmed`)
      } catch (error) {
        logger.error(`Failed to confirm action ${action}:`, error)
      }
    },

    async reconnectScreener() {
      await this.updateFirebase({
        status: 'Active'
      })
    },

    async completedScreener() {
      this.confirmAction('Completed')
    },

    async updateFirebase(updateData: any) {
      if (this.isConnected && this.screenerWatcher) {
        try {
          await this.screenerWatcher.update({
            ...updateData,
            lastUpdated: FirebaseUtils.serverTimestamp()
          })
          logger.info('Firebase updated successfully')
        } catch (error) {
          logger.error('Failed to update Firebase:', error)
          throw error
        }
      } else {
        logger.warn('Not connected or Firebase watcher not initialized')
      }
    },

    async sendScreenerError(payload: ScreenerError) {
      const newPriority = payload.priority ?? 0
      const currentPriority = this.activeScreener?.error?.priority ?? 0

      // If the new error has a lower priority than the current error, don't update
      if (newPriority < currentPriority) return

      const logPayload: Record<string, any> = {
        type: payload.type,
        message: payload.message
      }
      if (payload.metadata !== undefined) {
        logPayload.metadata = payload.metadata
      }

      await this.logToDatabase('screener_error', logPayload)

      const errorPayload: Record<string, any> = {
        type: payload.type,
        message: payload.message
      }
      if (payload.metadata !== undefined) {
        errorPayload.metadata = payload.metadata
      }

      await this.updateFirebase({
        status: 'Error',
        error: errorPayload
      })
    },

    async sendScreenerResolvedError(payload: { type: progressSateError }) {
      if (!this.isConnected || !this.screenerWatcher) {
        logger.error('Not connected or Firebase watcher not initialized')
        return
      }
      try {
        const currentState = await this.screenerWatcher.get()
        if (currentState?.error?.type === payload.type) {
          const updated: Record<string, any> = {
            status: 'Active',
            error: null,
            priority: null
          }

          await this.updateFirebase(updated)
          await this.logToDatabase('screener_error_resolved', {
            type: payload.type
          })
        }
      } catch (error) {
        logger.error('Failed to resolve screener error:', error)
      }
    },

    async sendScreenerWarning(payload: ScreenerError) {
      const newPriority = payload.priority ?? 0
      const currentPriority = this.activeScreener?.error?.priority ?? 0

      // If the new warning has a lower priority than the current warning, don't update
      if (newPriority < currentPriority) return

      const logPayload: Record<string, any> = {
        type: payload.type,
        message: payload.message
      }
      if (payload.metadata !== undefined) {
        logPayload.metadata = payload.metadata
      }

      await this.logToDatabase('screener_warning', logPayload)

      const errorPayload: Record<string, any> = {
        type: payload.type,
        message: payload.message
      }
      if (payload.metadata !== undefined) {
        errorPayload.metadata = payload.metadata
      }

      await this.updateFirebase({
        status: 'Warning',
        warning: errorPayload
      })
    },

    async sendScreenerResolvedWarning(payload: { type: progressSateError }) {
      if (!this.isConnected || !this.screenerWatcher) {
        logger.error('Not connected or Firebase watcher not initialized')
        return
      }
      try {
        const currentState = await this.screenerWatcher.get()
        if (currentState?.warning?.type === payload.type) {
          const updated: Record<string, any> = {
            status: 'Active',
            warning: null,
            priority: null
          }

          await this.updateFirebase(updated)
          await this.logToDatabase('screener_warning_resolved', {
            type: payload.type
          })
        }
      } catch (error) {
        logger.error('Failed to resolve screener warning:', error)
      }
    },

    async sendSectionChange(payload: { section: string }) {
      const updateData: { section: string; status?: string } = {
        section: payload.section
      }

      if (payload.section === 'CodeInputView') {
        updateData.status = 'Warning'
      }
      if (this.activeScreener?.section === 'CodeInputView' && payload.section !== 'CodeInputView') {
        updateData.status = 'Active'
      }

      await this.logToDatabase('section_change', { section: payload.section })
      await this.updateFirebase(updateData)
    },

    async disconnect() {
      if (this.isConnected) {
        try {
          await this.disconnectWatcher()

          await AxiosInstance.post(`${url}/screener/leave/${this.roomId}`)
          if (this.screenerWatcher) {
            this.screenerWatcher.off()
            this.screenerWatcher = null
          }
          this.isConnected = false
          this.roomId = null
          this.screenerId = null
          this.pupilScreenerId = null

          this.setSentryUserData(null)
        } catch (error) {
          logger.error('Error during disconnect:', error)
        }
      }
    },

    async disconnectWatcher() {
      return await this.screenerWatcher.cancelOnDisconnectHandler((error: any) => {
        if (error) {
          logger.error('Failed to cancel onDisconnect handler:', error)
        } else {
          logger.info('onDisconnect handler successfully canceled')
        }
      })
    },

    async checkAndUpdateScreenerState() {
      if (this.isConnected && this.screenerWatcher) {
        try {
          const screenerData: ScreenerData = await new Promise((resolve, reject) => {
            this.screenerWatcher.once((data: any) => {
              if (data) {
                resolve(data)
              } else {
                reject(null)
              }
            })
          })
          this.connectionId = screenerData.connectionId

          if (!screenerData) return

          if (screenerData.teacherSynced === undefined) {
            await this.updateFirebase({
              teacherSynced: false
            })
          }

          if (screenerData.status === 'Error') {
            await this.updateFirebase({
              state: 'Active',
              error: null
            })
          }
          if (screenerData.status === 'Warning') {
            await this.updateFirebase({
              state: 'Active',
              warning: null
            })
          }
          if (screenerData.state === 'Refresh') {
            logger.info('Screener is in Refresh state, updating to Active')
            await this.updateFirebase({
              state: 'Active'
            })
          }
          if (screenerData.state === 'Paused') {
            this.dataStore.setPausedState(true)
          }
        } catch (error) {
          logger.error('Failed to check and update screener state:', error)
        }
      } else {
        logger.warn('Screener watcher is not connected or initialized')
      }
    },

    async handleScreenerRemoved() {
      logger.error('Screener has been deleted from Firebase, or was inactive during a ping')
      this.disconnectWatcher()
      this.isConnected = false
      this.roomId = null
      this.screenerId = null
      this.activeCode = null
      this.screenerWatcher.off()
      this.pupilScreenerId = null

      this.dataStore.setScreenerError(
        'Error',
        'Sorry, this screeners connection has been closed.\n Please refresh the page and try again. \n If the problem persists, please contact the support team.'
      )

      return
    },

    /*
     * This function is used to watch the access request for the screener where an active code already exists
     * @param screenerId: string - the screener ID of the user
     * @param roomId: string - the room ID of the screener
     * @param deviceId: string - the device ID of the user to make sure the right device gets granted access
     * @param callback: Function - callback function to be called when the access request is approved for rejoining the screener
     */
    async watchAccessRequest(
      screenerId: string,
      roomId: string,
      deviceId: string,
      callback: Function
    ) {
      try {
        const accessRequestPath = `screenerRooms/${roomId}/accessRequests/${screenerId}`
        const accessRequestObject = await FirebaseUtils.createSyncedObject(null, accessRequestPath)

        let deviceRequestReceived = false
        accessRequestObject.on(async (request) => {
          if (!request) return
          const cancelDisconnectHandler = async () => {
            accessRequestObject.cancelOnDisconnectHandler((error: any) => {
              if (error) {
                logger.error('Failed to cancel onDisconnect handler:', error)
              } else {
                logger.info('onDisconnect handler successfully canceled')
              }
            })
          }

          const disconnectRequest = async () => {
            // Clean up the request after processing
            accessRequestObject.remove((error) => {
              if (error) {
                logger.error('Error removing access request', error)
              } else {
                logger.info('Access request removed successfully')
              }
            })

            cancelDisconnectHandler()
            // Stop listening for changes
            accessRequestObject.off()
          }

          if (request.deviceId === String(deviceId)) {
            deviceRequestReceived = true
            if (request.status === 'approved') {
              // Reconnect to the screener
              await callback()
            } else if (request.status === 'denied') {
              this.dataStore.setScreenerError(
                'Error',
                'Access request denied. Please try use a different code.'
              )
            } else if (request.status === 'pending') {
              return
            }
            disconnectRequest()
          } else {
            // Don't disconnect until the device request is received
            if (!deviceRequestReceived) return
            cancelDisconnectHandler()
            accessRequestObject.off()
            this.dataStore.setScreenerError(
              'Error',
              'Access request replaced by another device. Please re-enter the code to request access on this device.'
            )
          }
        }, true)

        accessRequestObject.setOnDisconnectHandler('remove', null, (error: any) => {
          if (error) {
            logger.error('Failed to set onDisconnect handler for access request:', error)
          } else {
            logger.info('onDisconnect handler set successfully for access request')
          }
        })
      } catch (error) {
        logger.error('Error in watching Access Request:', error)
      }
    },

    updateMediaDeviceOnSentryData() {
      const appendUpdatedData = this.getFormattedMediaDeviceInfo()
      this.sentryUserData = { ...this.sentryUserData, ...appendUpdatedData }
      this.updateSentryUserAndTags()
    },

    setSentryUserData(userData: ApiPupilScreener | null) {
      if (userData === null) {
        this.sentryUserData = {}
        Sentry.setUser(null)
        // Clear all user-specific tags by setting them to null
        // Note: deviceUUID and schoolName are global tags and persist across users
        Sentry.setTags({
          code: null,
          screenerTemplateName: null,
          teacherId: null,
          classScreenerId: null,
          pupilId: null,
          screenerId: null,
          outputDevice: null,
          inputDevice: null,
          outputDeviceGroupId: null,
          inputDeviceGroupId: null,
          outputDeviceId: null,
          inputDeviceId: null,
          availableInputDevices: null,
          availableOutputDevices: null
        })
        return
      }

      const {
        pupil_id: pupilId,
        full_name: pupilName,
        teacher_id: teacherId,
        id,
        code,
        class_id: classScreenerId,
        school_name: schoolName,
        screener_template_name: screenerTemplateName
      } = userData

      const formattedMediaDeviceInfo = this.getFormattedMediaDeviceInfo()
      const deviceUUID = localStorage.getItem('mable_device_uuid') || 'unknown'

      // Store school name for this device - helps track errors before code entry on subsequent loads
      if (schoolName) {
        localStorage.setItem('mable_device_school', schoolName)
      }

      this.sentryUserData = {
        pupilId,
        pupilName,
        teacherId,
        classScreenerId,
        screenerId: id,
        code,
        schoolName,
        screenerTemplateName,
        deviceUUID,
        ...formattedMediaDeviceInfo
      }

      this.updateSentryUserAndTags()
    },
    hasErrorState(errorType: string) {
      return this.activeScreener?.error?.type === errorType
    },
    hasWarningState(warningType: string) {
      return this.activeScreener?.warning?.type === warningType
    },
    isIntroOrOutro() {
      return (
        this.activeScreener?.section === 'ScreenerOutro' ||
        this.activeScreener?.section === 'ScreenerIntro'
      )
    },

    getFormattedMediaDeviceInfo() {
      const outputDeviceInfo = this.mediaStore.getCurrentDeviceInfo('output')
      const inputDeviceInfo = this.mediaStore.getCurrentDeviceInfo('input')
      const availableDevices: DeviceInfo[] = Array.isArray(this.mediaStore?.audioDevices)
        ? this.mediaStore.audioDevices
        : []

      const summarizeDevice = (device?: DeviceInfo | null) => {
        return device?.label || device?.deviceId || 'unknown'
      }

      const summarizeAvailableDevices = (deviceKind: 'audioinput' | 'audiooutput') => {
        const devicesForKind = availableDevices.filter(
          (device: DeviceInfo) => device.kind === deviceKind
        )
        if (!devicesForKind.length) return 'none'

        return devicesForKind
          .map((device: DeviceInfo) => {
            const label = device.label || device.deviceId || 'unknown'
            const deviceId = device.deviceId || 'unknown'
            const groupId = device.groupId || 'unknown'
            return `${label} [id:${deviceId},group:${groupId}]`
          })
          .join(' || ')
      }

      return {
        outputDevice: summarizeDevice(outputDeviceInfo),
        outputDeviceId: outputDeviceInfo?.deviceId || 'unknown',
        outputDeviceGroupId: outputDeviceInfo?.groupId || 'unknown',
        inputDevice: summarizeDevice(inputDeviceInfo),
        inputDeviceId: inputDeviceInfo?.deviceId || 'unknown',
        inputDeviceGroupId: inputDeviceInfo?.groupId || 'unknown',
        availableInputDevices: summarizeAvailableDevices('audioinput'),
        availableOutputDevices: summarizeAvailableDevices('audiooutput')
      }
    },

    updateSentryUserAndTags() {
      // Set standard Sentry user fields only (not searchable, but shows in user context)
      Sentry.setUser({
        id: String(this.sentryUserData.pupilId),
        username: this.sentryUserData.pupilName
      })

      // Set all searchable fields as tags (searchable via code:2222, schoolName:"School", etc.)
      Sentry.setTags({
        code: String(this.sentryUserData.code),
        schoolName: this.sentryUserData.schoolName,
        screenerTemplateName: this.sentryUserData.screenerTemplateName,
        teacherId: String(this.sentryUserData.teacherId),
        classScreenerId: String(this.sentryUserData.classScreenerId),
        pupilId: String(this.sentryUserData.pupilId),
        screenerId: String(this.sentryUserData.screenerId),
        deviceUUID: this.sentryUserData.deviceUUID,
        outputDevice: this.sentryUserData.outputDevice,
        inputDevice: this.sentryUserData.inputDevice,
        outputDeviceGroupId: this.sentryUserData.outputDeviceGroupId,
        inputDeviceGroupId: this.sentryUserData.inputDeviceGroupId,
        outputDeviceId: this.sentryUserData.outputDeviceId,
        inputDeviceId: this.sentryUserData.inputDeviceId,
        availableInputDevices: this.sentryUserData.availableInputDevices,
        availableOutputDevices: this.sentryUserData.availableOutputDevices
      })
    }
  }
})
